import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Button, Checkbox, checkboxClasses, List, ListItem, ListItemText } from "@mui/material";
import { axiosRequest } from "clients/api";
import Loader from "components/common/loader";
import ErrorPopup from "components/complex/dialogs/error-popup";
import { AZURE_STORAGE_PREFIX, DIGITALOCEAN_SPACES_STORAGE_URL } from "constant/env-variables";
import Message from "./message";
import { JSONParse, getQueryParams } from "utils/helpers";
import { isEmpty } from "lodash";

const layoutSx = {
  boxShadow: "inset 0 0 100px 15px #00000036",
  minHeight: "100vh"
};

const wrapperSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  pt: 2,
  pb: 2,
  width: "100%",
  minHeight: "100vh"
};

const infoSx = {
  width: "40%",
  margin: "0 auto",
  textAlign: "center",
  mb: 4
};

const unsubscriptionSx = {
  padding: "20px",
  maxWidth: "500px",
  width: "500px",
  borderRadius: "5px",
  backgroundColor: "grey.300"
};

const logoSx = {
  width: "200px",
  mb: 1,
  mr: "auto",
  ml: "auto",
  color: "primary.main"
};

const actionsSx = {
  display: "flex",
  justifyContent: "center",
  "& > .MuiButton-root": {
    marginRight: "10px",
    backgroundColor: "grey.400",
    color: "black"
  }
};

const UserUnsubscription = () => {
  const query = getQueryParams();

  const contactData = useMemo(() => {
    const defaultContactData = {
      tenant_name: "",
      tenant_logo: "",
      contact_id: "",
      tenant_id: "",
      first_name: "",
      last_name: "",
      unsubscribe: false,
      sub_seq: []
    };
    const values = JSONParse(atob(query.data));

    return isEmpty(values) ? defaultContactData : values;
  }, [query]);

  const sequencesSubscriptionsOptions = useMemo(
    () => contactData?.sub_seq.map((item) => ({ ...item, checked: true })),
    []
  );
  const [sequencesSubscriptions, setSequencesSubscriptions] = useState(sequencesSubscriptionsOptions || []);
  const [unsubscribeFromAllSequences, setUnsubscribeFromAllSequences] = useState(contactData.unsubscribe);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(undefined);
  const [isUnsubscriptionSucceed, setIsUnsubscriptionSucceed] = useState(false);
  const theme = useTheme();

  const onSubmit = () => {
    const data = {
      contact_id: contactData.contact_id,
      tenant_id: contactData.tenant_id,
      contact_first_name: contactData.first_name,
      contact_last_name: contactData.last_name,
      tenant_logo: contactData.tenant_logo,
      sub_seq: sequencesSubscriptions.filter((item) => !item.checked).map((item) => item.sequence_id),
      unsubscribe: contactData.unsubscribe === unsubscribeFromAllSequences ? null : unsubscribeFromAllSequences
    };
    setIsLoading(true);
    axiosRequest("put", `/base/subscription_management/${contactData.contact_id}`, data)
      .then(() => {
        setIsUnsubscriptionSucceed(true);
      })
      .catch((e) => {
        setError("");
      })
      .finally(() => setIsLoading(false));
  };

  const onChangeHandler = (e, id) => {
    setSequencesSubscriptions((prev) =>
      prev.map((item) => (item.sequence_id === id ? { ...item, checked: e.target.checked } : item))
    );
  };

  const unsubscribeFromAllHandler = (e) => {
    setUnsubscribeFromAllSequences(e.target.checked);
    setSequencesSubscriptions((prev) => prev.map((item) => ({ ...item, checked: e.target.checked })));
  };

  return (
    <Box sx={layoutSx}>
      {isLoading && <Loader />}
      <Box sx={wrapperSx}>
        <Box sx={infoSx}>
          <Box sx={logoSx}>
            <img
              style={{ maxWidth: "100%" }}
              src={`${AZURE_STORAGE_PREFIX || DIGITALOCEAN_SPACES_STORAGE_URL}/${contactData.tenant_id}/companyLogo/${
                contactData?.tenant_logo
              }`}
              alt="Organization Logo"
            />
          </Box>
          <h2>{contactData?.tenant_name}</h2>
          <span>Subscription Management for: {`${contactData.first_name} ${contactData.last_name}`}</span>
        </Box>
        {isUnsubscriptionSucceed ? (
          <Message firstName={`${contactData.first_name}`} />
        ) : (
          <Box sx={unsubscriptionSx}>
            <p>{`${contactData?.first_name}`}, please check/uncheck your subscription preferences</p>
            <div style={{ height: "200px", marginBottom: "25px", overflowY: "auto" }}>
              {sequencesSubscriptionsOptions.length > 0 && (
                <List>
                  <ListItem sx={{ mb: "15px" }}>
                    <Checkbox
                      sx={{
                        [`&.${checkboxClasses.checked}`]: {
                          color: theme.palette.grey[400]
                        }
                      }}
                      onClick={unsubscribeFromAllHandler}
                      checked={unsubscribeFromAllSequences}
                      disableRipple
                    />
                    <ListItemText
                      primary={`Unsubscribe All-Removes you from all ${contactData?.tenant_name} Marketing `}
                    />
                  </ListItem>
                  {sequencesSubscriptions.map((item) => (
                    <ListItem key={item.sequence_id}>
                      <Checkbox
                        sx={{
                          [`&.${checkboxClasses.checked}`]: {
                            color: theme.palette.grey[400]
                          }
                        }}
                        onChange={(e) => onChangeHandler(e, item.sequence_id)}
                        checked={item.checked}
                        disableRipple
                      />
                      <ListItemText primary={item.name} />
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
            <Box sx={actionsSx}>
              <Button variant="contained" onClick={onSubmit}>
                Submit
              </Button>
              {/* <Button variant="contained">Cancel</Button> */}
            </Box>
          </Box>
        )}
      </Box>
      <ErrorPopup
        submitFunction={onSubmit}
        submitButton="Retry"
        isOpen={error !== undefined}
        onClose={() => setError(undefined)}
        message={error}
      />
    </Box>
  );
};

export default UserUnsubscription;
